import { useEffect, useState } from "react"

import { ArrowLeftIcon } from "@heroicons/react/20/solid"
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  LockClosedIcon,
} from "@heroicons/react/24/solid"
import { useQueryClient } from "@tanstack/react-query"
import { clsx } from "clsx"
import type { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom"

import { queryKeys } from "~/api/cache"
import type { SolarProposalDto } from "~/api/dto/solar-proposal.dto"
import useSolarProposalSummary from "~/api/hooks/useSolarProposalSummary"
import powurLogoWhite from "~/assets/svg/powur-logo-white.svg?url"
import { parseAxiosError } from "~/utils/parseAxiosError"

import {
  formatPersonPhone,
  personName,
} from "../../BatteryOnlyProposal/person-display"
import { PINNED_BATTERY_PROPOSAL_THEME } from "../../BatteryOnlyProposal/theme/battery-proposal-theme"
import {
  draftedFundingChosen,
  EMPTY_EQUIPMENT_DRAFT,
  type SolarEquipmentDraft,
} from "../solar-equipment-form"
import { powurProposalsUrl } from "../solar-urls"
import { SolarConsumptionStep } from "../SolarConsumptionStep"
import { SolarEquipmentStep } from "../SolarEquipmentStep"
import { SolarGreenbuttonStep } from "../SolarGreenbuttonStep"
import { SolarUtilityStep } from "../SolarUtilityStep"
import {
  SOLAR_SUMMARY_STEPS,
  solarSummaryGreenButtonPath,
  readSummaryReturnTo,
  solarSummaryRouteFromPath,
  solarSummaryStepPath,
  summaryProgress,
  summaryStepsMet,
  visibleSummarySteps,
  type SolarSummaryStep,
  type SolarSummaryStepId,
  type SolarSummaryStepState,
} from "./solar-summary-steps"
import { SolarSummaryLoanProductStep } from "./SolarSummaryLoanProductStep"
import {
  CONFIRM_AND_OPEN_LABEL,
  SUMMARY_CONFIRM_STAGE_LABELS,
  useSolarSummaryConfirm,
} from "./useSolarSummaryConfirm"

const STEP_BY_REQUIREMENT_STEP: Record<string, SolarSummaryStepId> =
  Object.fromEntries(
    SOLAR_SUMMARY_STEPS.map((step) => [step.requirementStep, step.id]),
  )

const stepTitle = (step: SolarSummaryStep, t: TFunction): string =>
  `titleKey` in step ? t(step.titleKey) : step.title

/** Router state every Summary location carries: where Confirm & open lands. */
type SummaryLocationState = { returnTo: string }

const StepIcon = ({ state }: { state: SolarSummaryStepState }): JSX.Element =>
  state === `done` ? (
    <CheckCircleIcon aria-hidden className="size-5 shrink-0 text-bp-success" />
  ) : state === `locked` ? (
    <LockClosedIcon aria-hidden className="size-5 shrink-0 text-bp-text-dim" />
  ) : (
    <ExclamationCircleIcon
      aria-hidden
      className={clsx(
        `size-5 shrink-0`,
        state === `current` ? `text-bp-accent` : `text-bp-text-muted`,
      )}
    />
  )

const STATE_LABELS: Record<SolarSummaryStepState, string> = {
  current: `current step`,
  done: `done`,
  locked: `locked`,
  open: `not done`,
}

/** V1's Summary menu: the homeowner, read-only, then Project Setup. */
const SummaryMenu = ({
  proposal,
  proposalId,
  steps,
  states,
  linkState,
}: {
  proposal: SolarProposalDto
  proposalId: string
  steps: readonly SolarSummaryStep[]
  states: Record<SolarSummaryStepId, SolarSummaryStepState>
  /** Carried on every Summary link, so the return target survives the steps. */
  linkState: SummaryLocationState
}): JSX.Element => {
  const { t } = useTranslation()
  const { homeowner, address } = proposal
  const phone = homeowner ? formatPersonPhone(homeowner.phone) : null

  return (
    <aside
      aria-label="Project Setup"
      className="hidden w-72 shrink-0 flex-col gap-6 lg:flex"
      data-testid="solar-summary-menu"
    >
      <section className="flex flex-col gap-1">
        <h2 className="font-display text-[length:var(--step-1)] font-semibold text-bp-text">
          {homeowner ? personName(homeowner, `Homeowner`) : `Homeowner`}
        </h2>
        <p className="text-[length:var(--step--1)] text-bp-text-soft">
          {address.street}
          <br />
          {`${address.city}, ${address.state} ${address.postal}`}
        </p>
        {phone ? (
          <p className="text-[length:var(--step--1)] text-bp-text-soft">
            {phone}
          </p>
        ) : null}
        {homeowner?.email ? (
          <p className="truncate text-[length:var(--step--1)] text-bp-text-soft">
            {homeowner.email}
          </p>
        ) : null}
      </section>
      <div className="h-px bg-bp-border-strong" />
      <section className="flex flex-col gap-3">
        <div>
          <h2 className="font-display text-[length:var(--step-0)] font-semibold text-bp-text">
            {t(`projectSetup`)}
          </h2>
          <p className="text-[length:var(--step--2)] text-bp-text-muted">
            {t(`summaryListDesc`)}
          </p>
        </div>
        <ol className="flex flex-col gap-1">
          {steps.map((step) => {
            const state = states[step.id]
            const title = stepTitle(step, t)
            return (
              <li
                key={step.id}
                data-testid={`solar-summary-step-${step.id}`}
                data-state={state}
                className={clsx(
                  `flex items-center gap-3 rounded-md border-l-[3px] px-3 py-2.5`,
                  state === `current`
                    ? `border-bp-accent bg-bp-glass-hi`
                    : `border-transparent`,
                )}
              >
                <StepIcon state={state} />
                {state === `locked` ? (
                  <span className="text-[length:var(--step--1)] font-semibold text-bp-text-dim">
                    {title}
                    <span className="sr-only"> ({STATE_LABELS[state]})</span>
                  </span>
                ) : (
                  <Link
                    to={solarSummaryStepPath(proposalId, step.id)}
                    state={linkState}
                    aria-current={state === `current` ? `step` : undefined}
                    className={clsx(
                      `text-[length:var(--step--1)] font-semibold hover:text-bp-accent`,
                      state === `current` ? `text-bp-accent` : `text-bp-text`,
                    )}
                  >
                    {title}
                    <span className="sr-only"> ({STATE_LABELS[state]})</span>
                  </Link>
                )}
              </li>
            )
          })}
        </ol>
      </section>
    </aside>
  )
}

/** Phone width: the menu collapses to the step and progress dots. */
const SummaryStepHeader = ({
  proposal,
  steps,
  current,
  states,
}: {
  proposal: SolarProposalDto
  steps: readonly SolarSummaryStep[]
  current: SolarSummaryStepId
  states: Record<SolarSummaryStepId, SolarSummaryStepState>
}): JSX.Element => {
  const index = steps.findIndex((step) => step.id === current)
  return (
    <div className="flex flex-col gap-2 lg:hidden">
      <p className="truncate text-[length:var(--step--1)] text-bp-text-soft">
        {proposal.homeowner
          ? personName(proposal.homeowner, `Homeowner`)
          : `Homeowner`}
        {` · `}
        {proposal.address.street}
      </p>
      <div className="flex items-center gap-3">
        <span className="text-[length:var(--step--2)] font-semibold tracking-wide text-bp-text-muted uppercase">
          Step {index + 1} of {steps.length}
        </span>
        <ol aria-hidden className="flex gap-1.5">
          {steps.map((step) => (
            <li
              key={step.id}
              className={clsx(
                `size-2 rounded-full`,
                states[step.id] === `current`
                  ? `bg-bp-accent`
                  : states[step.id] === `done`
                    ? `bg-bp-success`
                    : `bg-bp-border-strong`,
              )}
            />
          ))}
        </ol>
      </div>
    </div>
  )
}

const ConfirmProgress = ({ label }: { label: string }): JSX.Element => (
  <div
    role="dialog"
    aria-modal="true"
    aria-label="Opening proposal"
    className="fixed inset-0 z-[2147483001] flex items-center justify-center bg-[rgba(2,4,10,0.72)] px-4"
  >
    <div className="flex items-center gap-3 rounded-xl border border-bp-border-strong bg-bp-elevated px-6 py-5 shadow-lg">
      <span
        aria-hidden
        className="size-5 animate-spin rounded-full border-2 border-bp-accent border-t-transparent"
      />
      <p
        role="status"
        data-testid="solar-summary-confirm-stage"
        className="text-[length:var(--step--1)] font-semibold text-bp-text"
      >
        {label}
      </p>
    </div>
  </div>
)

/**
 * Solar Proposal V2 Summary (VIS-804): V1's pre-proposal setup wizard, V2
 * styled, every read and save through V2 REST. A left menu with the homeowner
 * and Project Setup, and a step card reusing the Setup drawers' forms. The
 * last step (Equipment, or a Credit Human plan's Loan Product) runs Refresh
 * Pricing, Recalculate Savings and the server-checked confirm, which opens the
 * proposal.
 */
export const SolarSummary = ({
  proposal,
  proposalId,
  headerFailed,
  onRetryHeader,
}: {
  proposal: SolarProposalDto
  proposalId: string
  /** The shell's header read failed again; the proposal shown may be stale. */
  headerFailed: boolean
  onRetryHeader: () => void
}): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { hash, pathname, search, state: locationState } = useLocation()
  const queryClient = useQueryClient()
  const summaryQuery = useSolarProposalSummary(proposalId)
  // The link that met the Summary, kept for Confirm & open. Only a path on
  // this proposal outside the Summary is followed; else the proposal opens.
  const [returnTo] = useState(() =>
    readSummaryReturnTo(locationState, proposalId),
  )
  const confirm = useSolarSummaryConfirm(proposalId, returnTo)
  const linkState: SummaryLocationState = { returnTo }
  const confirmedElsewhere = summaryQuery.data?.confirmedAt != null
  // Confirmed in V1, another tab or by a co-seller since the header was read:
  // read the header again, and the shell opens the proposal.
  useEffect(() => {
    if (confirmedElsewhere) {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.byId(proposalId),
        exact: true,
      })
    }
  }, [confirmedElsewhere, proposalId, queryClient])
  /**
   * Steps the seller moved past this session. As in V1, saved values never
   * count as reviewed on their own: each visit starts at the first step, with
   * the forms showing what the server holds.
   */
  const [continued, setContinued] = useState<ReadonlySet<SolarSummaryStepId>>(
    () => new Set(),
  )
  /** Funding's choice, saved with the equipment by the Equipment step. */
  const [equipmentDraft, setEquipmentDraft] = useState<SolarEquipmentDraft>(
    EMPTY_EQUIPMENT_DRAFT,
  )

  const route = solarSummaryRouteFromPath(pathname, proposalId) ?? {
    step: null,
    greenButton: false,
  }

  const frame = (children: JSX.Element): JSX.Element => (
    <div
      data-testid="solar-summary"
      data-theme="dark"
      data-battery-theme={PINNED_BATTERY_PROPOSAL_THEME}
      className="min-h-screen bg-bp-page text-bp-text"
    >
      <header className="flex items-center justify-between gap-4 border-b border-bp-border bg-bp-base px-4 py-4 sm:px-8">
        <div className="flex items-center gap-4">
          <img src={powurLogoWhite} alt="Powur" className="h-[22px] w-auto" />
          <div className="h-[20px] w-px bg-bp-border-strong" />
          <span className="text-[length:var(--step--2)] font-semibold tracking-[0.18em] text-bp-text-dim uppercase">
            Solar Proposal
          </span>
        </div>
        {/* Leaves Vision, so a plain same-tab anchor, as the Setup rail's is. */}
        <a
          href={powurProposalsUrl(proposal.powurProjectId)}
          className="flex items-center gap-1.5 text-[length:var(--step--1)] font-semibold text-bp-text-soft hover:text-bp-text"
        >
          <ArrowLeftIcon aria-hidden className="size-4" />
          {t(`backToPowur`)}
        </a>
      </header>
      {children}
    </div>
  )

  if (!summaryQuery.data || confirmedElsewhere) {
    // Confirmed elsewhere waits on the header read, which can fail too.
    const failed = confirmedElsewhere ? headerFailed : summaryQuery.isError
    return frame(
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 px-6 text-center">
        <p role="status">
          {!failed
            ? `Loading Summary`
            : confirmedElsewhere
              ? `Unable to load Solar Proposal`
              : parseAxiosError(
                  summaryQuery.error,
                  `Unable to load the Summary`,
                )}
        </p>
        {failed ? (
          <button
            type="button"
            className="text-[length:var(--step--1)] font-semibold text-bp-accent underline"
            onClick={() =>
              confirmedElsewhere ? onRetryHeader() : void summaryQuery.refetch()
            }
          >
            Retry
          </button>
        ) : null}
      </div>,
    )
  }

  const summary = summaryQuery.data
  // Loan Product shows only for a Credit Human, Sungage or LightReach plan, drafted or stored.
  const steps = visibleSummarySteps({
    summary,
    draftedFinanceType: equipmentDraft.financeType,
  })
  const loanProductStep = steps.some((entry) => entry.id === `loan-product`)
  const serverMet = summaryStepsMet(summary)
  // A partner drafted on Funding counts until Equipment saves it; Sungage
  // drafted without its funding group doesn't.
  const met = {
    ...serverMet,
    [`funding-selection`]:
      serverMet[`funding-selection`] || draftedFundingChosen(equipmentDraft),
  }
  const { states, resume, isUnlocked } = summaryProgress({
    steps,
    met,
    continued,
    current: route.step,
  })

  // `/summary`, an unknown, hidden or locked step goes to the first step not done.
  if (route.step === null || !isUnlocked(route.step)) {
    return (
      <Navigate
        to={`${solarSummaryStepPath(proposalId, resume)}${search}${hash}`}
        state={linkState}
        replace
      />
    )
  }

  const current = route.step
  const step = steps.find((entry) => entry.id === current)!
  const stepIndex = steps.indexOf(step)
  const stepPath = (id: SolarSummaryStepId): string =>
    solarSummaryStepPath(proposalId, id)

  /** The step's save (or Continue) succeeded: read readiness again, move on. */
  const continueFrom = async (id: SolarSummaryStepId): Promise<void> => {
    // An earlier failure no longer describes where the seller is.
    confirm.reset()
    await summaryQuery.refetch()
    setContinued(new Set([...continued, id]))
    const next = steps[stepIndex + 1]
    if (next) {
      navigate(stepPath(next.id), { state: linkState })
    }
  }
  const onContinue = (): void => {
    void continueFrom(current)
  }

  // Moved past, yet the server still finds it incomplete: say what's missing.
  const stillMissing = continued.has(current)
    ? summary.requirements.filter(
        (requirement) =>
          !requirement.met &&
          STEP_BY_REQUIREMENT_STEP[requirement.step] === current &&
          !(current === `funding-selection` && met[current]),
      )
    : []
  const confirmMissing =
    confirm.failure?.stage === `confirming`
      ? summary.requirements.filter((requirement) => !requirement.met)
      : []

  return frame(
    <>
      <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-6 sm:px-8 lg:flex-row lg:gap-16 lg:py-12">
        <SummaryMenu
          proposal={proposal}
          proposalId={proposalId}
          steps={steps}
          states={states}
          linkState={linkState}
        />
        <div className="flex min-w-0 max-w-3xl flex-1 flex-col gap-4">
          <SummaryStepHeader
            proposal={proposal}
            steps={steps}
            current={current}
            states={states}
          />
          <section
            aria-labelledby="solar-summary-step-title"
            data-testid="solar-summary-card"
            className="flex flex-col gap-6 rounded-xl border border-bp-border-strong bg-bp-elevated p-5 sm:p-8"
          >
            <h1
              id="solar-summary-step-title"
              className="font-display text-[length:var(--step-2)] font-semibold text-bp-text"
            >
              {stepTitle(step, t)}
            </h1>

            {stillMissing.length > 0 ? (
              <ul
                role="status"
                className="list-disc rounded-lg border border-bp-border-strong bg-bp-glass p-4 pl-8 text-[length:var(--step--1)] text-bp-text-soft"
              >
                {stillMissing.map((requirement) => (
                  <li key={requirement.key}>{requirement.message}</li>
                ))}
              </ul>
            ) : null}

            {confirm.failure ? (
              <div
                role="alert"
                data-testid="solar-summary-confirm-error"
                className="flex flex-col gap-2 rounded-lg border border-bp-danger/40 bg-bp-danger/10 p-4 text-[length:var(--step--1)] text-bp-text"
              >
                <p>{confirm.failure.message}</p>
                {confirmMissing.length > 0 ? (
                  <ul className="list-disc pl-5">
                    {confirmMissing.map((requirement) => (
                      <li key={requirement.key}>
                        <Link
                          to={stepPath(
                            STEP_BY_REQUIREMENT_STEP[requirement.step],
                          )}
                          state={linkState}
                          className="font-semibold text-bp-accent underline"
                        >
                          {requirement.message}
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}

            {current === `utility-information` ? (
              <SolarUtilityStep
                proposalId={proposalId}
                summary={{ onContinue }}
              />
            ) : current === `energy-consumption` ? (
              <SolarConsumptionStep
                proposalId={proposalId}
                summary={{
                  onContinue,
                  greenButtonPath: solarSummaryGreenButtonPath(proposalId),
                }}
              />
            ) : current === `loan-product` ? (
              <SolarSummaryLoanProductStep
                proposalId={proposalId}
                onConfirm={() => void confirm.run()}
                busy={confirm.stage !== null}
              />
            ) : (
              <SolarEquipmentStep
                key={current}
                proposalId={proposalId}
                onSaved={onContinue}
                summary={
                  current === `funding-selection`
                    ? {
                        part: `funding`,
                        draft: equipmentDraft,
                        onDraftChange: setEquipmentDraft,
                        onContinue,
                        continueLabel: `Continue`,
                        busy: false,
                      }
                    : {
                        part: `equipment`,
                        draft: equipmentDraft,
                        onDraftChange: setEquipmentDraft,
                        // Its save stores the plan Loan Product's selection
                        // needs, so it moves on there; else it confirms.
                        ...(loanProductStep
                          ? {
                              onContinue,
                              continueLabel: `Continue`,
                              busy: false,
                            }
                          : {
                              onContinue: () => void confirm.run(),
                              continueLabel: CONFIRM_AND_OPEN_LABEL,
                              busy: confirm.stage !== null,
                            }),
                      }
                }
              />
            )}
          </section>
        </div>
      </div>

      {route.greenButton ? (
        <SolarGreenbuttonStep
          onClose={() =>
            navigate(stepPath(`energy-consumption`), { state: linkState })
          }
          onRecalculateSavings={() =>
            navigate(stepPath(`energy-consumption`), { state: linkState })
          }
          proposal={proposal}
          proposalId={proposalId}
        />
      ) : null}

      {confirm.stage ? (
        <ConfirmProgress label={SUMMARY_CONFIRM_STAGE_LABELS[confirm.stage]} />
      ) : null}
    </>,
  )
}
